import { ensureRouteQueryData } from "@/lib/route-loader";
import { useOrganizationParentGetOrganizationSettings } from "@/orval/organization-parent";
import { getOrganizationParentGetOrganizationSettingsQueryOptions } from "@/orval/organization-parent";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
  UserCheckIcon,
  CreditCard,
  TableIcon,
  ChevronRight,
  BookOpenIcon,
  HomeIcon,
  GraduationCapIcon,
  NewspaperIcon,
  UsersIcon,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarSeparator,
  SidebarProvider,
} from "@/components/ui/sidebar";
import { SidebarOrganizationHeader } from "./-components/sidebar-organization-header";
import { useState } from "react";
import { useAuthGetImpersonationStatus } from "@/orval/auth";
import { cn } from "@/lib/utils";
import { parseOrganizationIdParams } from "@/lib/route-params";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { LucideIcon } from "lucide-react";
import { SidebarViewSwitcher } from "./-components/sidebar-footer-actions";
import { useSidebar } from "@/hooks/use-sidebar";
import { ProductEmailInvitation } from "@/components/product-email-invitation";

export const Route = createFileRoute("/_app/organizations/$organizationId/parent")({
  staticData: { sidebarLayout: true },
  params: {
    parse: parseOrganizationIdParams,
  },
  loader: ({ params, ...loaderContext }) =>
    ensureRouteQueryData(
      loaderContext,
      getOrganizationParentGetOrganizationSettingsQueryOptions(params.organizationId),
    ),
  component: RouteComponent,
});

type SidebarChildItem = {
  to: string;
  label: string;
  icon: LucideIcon;
};

type SidebarNavItem = {
  label: string;
  icon: LucideIcon;
  to?: string;
  children?: SidebarChildItem[];
};

const navigationItems: SidebarNavItem[] = [
  {
    label: "Academics",
    icon: BookOpenIcon,
    children: [
      {
        to: "/organizations/$organizationId/parent/feed",
        icon: NewspaperIcon,
        label: "Updates",
      },
      {
        to: "/organizations/$organizationId/parent/classes",
        icon: BookOpenIcon,
        label: "Classes",
      },
      {
        to: "/organizations/$organizationId/parent/enrollment",
        icon: UserCheckIcon,
        label: "Enrollment",
      },
      {
        to: "/organizations/$organizationId/parent/schedule",
        icon: TableIcon,
        label: "Schedule",
      },
    ],
  },
  {
    label: "Forms",
    icon: TableIcon,
    to: "/organizations/$organizationId/parent/forms",
  },
  {
    label: "Family",
    icon: HomeIcon,
    children: [
      {
        to: "/organizations/$organizationId/parent/students",
        icon: GraduationCapIcon,
        label: "Students",
      },
      {
        to: "/organizations/$organizationId/parent/adults",
        icon: UsersIcon,
        label: "Adults",
      },
      {
        to: "/organizations/$organizationId/parent/payments",
        icon: CreditCard,
        label: "Payments",
      },
    ],
  },
];

function RouteComponent() {
  const { organizationId } = Route.useParams();
  const { data: parentOrganizationSettings } = useOrganizationParentGetOrganizationSettings(organizationId);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { data: impersonationStatus } = useAuthGetImpersonationStatus();

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="flex min-w-0 flex-1 flex-col min-h-full w-full">
        <div className="flex min-w-0 flex-row flex-1">
          <OrganizationParentSidebar
            organizationId={organizationId}
            organizationName={parentOrganizationSettings?.organizationName ?? undefined}
            logoUrl={parentOrganizationSettings?.organizationLogoUrl}
            isImpersonating={!!impersonationStatus?.isImpersonating}
          />

          <div className="flex min-w-0 flex-col flex-1 md:w-auto w-full">
            <ProductEmailInvitation />
            <Outlet />
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}

type OrganizationParentSidebarProps = {
  organizationId: number;
  organizationName?: string;
  logoUrl?: string | null;
  isImpersonating: boolean;
};

const OrganizationParentSidebar = ({
  organizationId,
  organizationName,
  logoUrl,
  isImpersonating,
}: OrganizationParentSidebarProps) => {
  const { isMobile, open, setOpenMobile } = useSidebar();
  const isCollapsedDesktop = !open && !isMobile;
  const handleNavigationLinkClick = () => {
    if (isMobile) {
      setOpenMobile(false);
    }
  };

  return (
    <Sidebar
      className={cn(
        isImpersonating ? "md:top-28 md:pb-28" : "md:top-16 md:pb-16",
        `w-64 border-r-muted border-r flex-none bg-background z-10`,
      )}
      collapsible="icon"
    >
      <SidebarHeader className="bg-background group-data-[collapsible=icon]:p-1">
        <SidebarOrganizationHeader
          logoUrl={logoUrl}
          organizationName={organizationName}
          organizationId={organizationId}
        />
      </SidebarHeader>

      <SidebarContent className="bg-background">
        <SidebarGroup className="pt-0">
          <SidebarGroupContent>
            <SidebarMenu>
              {navigationItems.map((item) => {
                if (!item.children && item.to) {
                  return (
                    <SidebarMenuItem key={item.to}>
                      <SidebarMenuButton asChild tooltip={item.label}>
                        <Link
                          to={item.to}
                          from={Route.fullPath}
                          activeProps={{ className: "bg-secondary text-secondary-foreground/100" }}
                          onClick={handleNavigationLinkClick}
                        >
                          <item.icon />
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                if (!item.children) {
                  return null;
                }

                const defaultOpen = true;

                if (isCollapsedDesktop) {
                  return (
                    <SidebarMenuItem key={item.label}>
                      <Popover>
                        <PopoverTrigger asChild>
                          <SidebarMenuButton tooltip={item.label}>
                            <item.icon />
                            <span>{item.label}</span>
                          </SidebarMenuButton>
                        </PopoverTrigger>
                        <PopoverContent side="right" align="start" className="w-56 p-2">
                          <p className="px-2 pb-2 text-xs font-medium text-muted-foreground">{item.label}</p>
                          <div className="flex flex-col gap-1">
                            {item.children.map((child) => (
                              <Link
                                key={child.to}
                                to={child.to}
                                from={Route.fullPath}
                                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-foreground/80 hover:bg-accent hover:text-accent-foreground"
                                activeProps={{
                                  className: "bg-secondary text-secondary-foreground/100",
                                }}
                              >
                                <child.icon className="size-4" />
                                <span>{child.label}</span>
                              </Link>
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <Collapsible key={item.label} defaultOpen={defaultOpen} className="group/collapsible">
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton tooltip={item.label}>
                          <item.icon />
                          <span>{item.label}</span>
                          <ChevronRight className="ml-auto transition-transform group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {item.children.map((child) => (
                            <SidebarMenuSubItem key={child.to}>
                              <SidebarMenuSubButton asChild>
                                <Link
                                  to={child.to}
                                  from={Route.fullPath}
                                  activeProps={{
                                    className: "bg-secondary text-secondary-foreground/100",
                                  }}
                                  onClick={handleNavigationLinkClick}
                                >
                                  <child.icon />
                                  <span>{child.label}</span>
                                </Link>
                              </SidebarMenuSubButton>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="bg-background">
        <SidebarSeparator className="mx-0" />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarViewSwitcher
              label="Switch view"
              currentView="Parent"
              organizationId={organizationId}
              from={Route.fullPath}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
